import { useEffect, useState } from 'react'
import { SidebarSearchInput } from '~/components/common/SidebarSearchInput'
import { useConversationsContext } from '~/contexts/ConversationContext'
import { useSidebarContext } from '~/contexts/SidebarContext'
import { bridge } from '~/services/bridge'
import { cm } from '~/utils'
import { Conversations } from './ConversationList'
import { SidebarHeader } from './SidebarHeader'
import { SidebarMenu, SidebarMenuMinimal } from './SidebarMenu'
import { SidebarTabs } from './SidebarTabs'
import { WorkspaceSwitcher } from './WorkspaceSwitcher'
export type TabType = 'chats' | 'tasks'

interface Props {
  handleRoot: () => void
  /** When true, hides workspace switcher and conversation list (for no-workspace state) */
  minimalMode?: boolean
  /** When true, sidebar fills its parent container width instead of fixed 304px */
  fullWidth?: boolean
}

/**
 * Minimal sidebar for no-workspace state.
 * Only shows header and user menu, no workspace switching or conversations.
 */
function SidebarMinimal({ handleRoot }: { handleRoot: () => void }) {
  const { collapsed, toggleCollapsed } = useSidebarContext()

  return (
    <div
      className={cm(
        'bg-tokens-neutral-bg-white flex flex-col h-full border-r border-tokens-neutral-border-2 overflow-hidden',
        collapsed ? 'w-[0px]' : 'w-[304px]'
      )}
    >
      <SidebarHeader handleCollapseClick={toggleCollapsed} handleRoot={handleRoot} />
      <div className='h-px bg-tokens-neutral-border-2' />
      <div className='flex-1' /> {/* Spacer to push menu to bottom */}
      <SidebarMenuMinimal />
    </div>
  )
}

/**
 * Full sidebar with workspace switching, conversations, and all features.
 * Requires ConversationContext to be available.
 */
function SidebarFull({ handleRoot, fullWidth }: { handleRoot: () => void; fullWidth?: boolean }) {
  const { collapsed, toggleCollapsed, setIsSearching, activeTab, setActiveTab } =
    useSidebarContext()
  const { searchQuery, setSearchQuery, setSearchType, refetch } = useConversationsContext()
  const [showSearch, setShowSearch] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const isPlugin = bridge.isInPlugin()

  // Update global search state when showSearch changes
  useEffect(() => {
    setIsSearching(showSearch)
  }, [showSearch, setIsSearching])

  // Update search type when view changes
  useEffect(() => {
    setSearchType(activeTab)
  }, [activeTab, setSearchType])

  // Debounce input before syncing to global query (fetching done on Enter/Blur)
  useEffect(() => {
    if (!showSearch) return
    const timer = setTimeout(() => {
      const value = searchInput.trim()
      // Only update search query if it's different from current one
      // Backend handles mapping query to task titles when type=tasks
      if (value.length >= 2 && value !== searchQuery) {
        setSearchQuery(value)
      } else if (value.length < 2 && searchQuery) {
        // Clear search when input is too short
        setSearchQuery('')
      }
    }, 800)
    return () => clearTimeout(timer)
  }, [searchInput, showSearch, searchQuery, setSearchQuery])

  const handleViewChange = (newView: TabType) => {
    setActiveTab(newView)
  }

  return (
    <>
      <div
        className={cm(
          'bg-tokens-neutral-bg-white flex flex-col h-full border-r border-tokens-neutral-border-2 overflow-hidden',
          fullWidth ? 'w-full' : collapsed ? 'w-[0px]' : 'w-[304px]'
        )}
      >
        <SidebarHeader handleCollapseClick={toggleCollapsed} handleRoot={handleRoot} />
        <WorkspaceSwitcher />
        <div className='h-px bg-tokens-neutral-border-2' />
        <div className={cm(isPlugin ? 'py-2' : 'py-4', 'relative')}>
          {!showSearch && (
            <SidebarTabs
              view={activeTab}
              setView={handleViewChange}
              onSearchClick={() => {
                setShowSearch(true)
                setSearchInput('')
              }}
            />
          )}
          {showSearch && (
            <div className={cm(isPlugin ? 'pl-3 pr-1' : 'pl-4 pr-1', 'flex items-center')}>
              <SidebarSearchInput
                placeholder={activeTab === 'tasks' ? 'Search task...' : 'Search chat...'}
                value={searchInput}
                onChange={v => setSearchInput(v)}
                onClear={() => {
                  setSearchInput('')
                  setSearchQuery('')
                }}
                onClose={() => {
                  setShowSearch(false)
                  setSearchInput('')
                  setSearchQuery('')
                }}
                onEnter={() => {
                  void refetch()
                }}
                className='w-full'
                autoFocus
              />
            </div>
          )}
        </div>
        <div className='h-px bg-tokens-neutral-border-2' />
        <div className='py-2 px-3 flex-1 overflow-auto'>
          <Conversations view={activeTab} />
        </div>
        <SidebarMenu />
      </div>
    </>
  )
}

/**
 * Main Sidebar component that delegates to either minimal or full version.
 */
function Sidebar({ handleRoot, minimalMode = false, fullWidth }: Props) {
  if (minimalMode) {
    return <SidebarMinimal handleRoot={handleRoot} />
  }
  return <SidebarFull handleRoot={handleRoot} fullWidth={fullWidth} />
}

export default Sidebar
